// Settings → General: how the business prices catering (kitchen flow,
// migration 179). Per dish (the default — nothing changes for anyone who
// never opens this) or per guest. New bookings start this way; each booking
// can still be switched. Admin only, and only with kitchen_flow on — renders
// nothing otherwise, so live tenants see no new setting.
import { useMutation, useQueryClient } from '@tanstack/react-query'
import apiClient from '@/api/client'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { useAllSettings, useKitchenFlowEnabled } from '@/hooks/useKitchenFlowEnabled'
import { useToast } from '@/hooks/use-toast'
import { PGT } from '@/lib/inventoryFlow/cateringStrings'
import { CATERING_PRICING_KEY, parseCateringPricing, type PricingMode } from '@/lib/inventoryFlow/cateringLogic'
import { getStoredUserRole } from '@/lib/staff-roles'
import { cn } from '@/lib/utils'
import { plainError } from '@/components/store/inventory/prep/prepRecipeDraft'

export function CateringPricingSettingCard() {
  const enabled = useKitchenFlowEnabled()
  const current = parseCateringPricing(useAllSettings())
  const qc = useQueryClient()
  const { toast } = useToast()
  const save = useMutation({
    mutationFn: (mode: PricingMode) => apiClient.updateSetting(CATERING_PRICING_KEY, { mode }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['settings', 'all'] })
      toast({ title: PGT.settingSaved })
    },
    onError: (e: Error) => toast({ variant: 'destructive', title: plainError(e) }),
  })
  if (!enabled || getStoredUserRole() !== 'admin') return null

  // While a change is in flight the choice shows where it is going.
  const shown: PricingMode = save.isPending && save.variables ? save.variables : current
  const options: { value: PricingMode; label: string }[] = [
    { value: 'per_dish', label: PGT.settingPerDish },
    { value: 'per_guest', label: PGT.settingPerGuest },
  ]

  return (
    <Card>
      <CardHeader className="pb-4">
        <CardTitle className="text-base">{PGT.settingTitle}</CardTitle>
        <CardDescription>{PGT.settingHelp}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex w-fit max-w-full flex-wrap gap-1 rounded-lg bg-muted/60 p-1" role="radiogroup" aria-label={PGT.settingTitle}>
          {options.map((o) => (
            <button key={o.value} type="button" role="radio" aria-checked={shown === o.value} disabled={save.isPending}
              onClick={() => { if (o.value !== shown) save.mutate(o.value) }}
              className={cn('whitespace-nowrap rounded-md px-4 py-2 text-sm font-medium transition-colors disabled:opacity-60',
                shown === o.value ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground')}>
              {o.label}
            </button>
          ))}
        </div>
        <p className="text-sm text-muted-foreground">{shown === 'per_guest' ? PGT.settingPerGuestHelp : PGT.settingPerDishHelp}</p>
      </CardContent>
    </Card>
  )
}
